import React, { useState, useEffect } from 'react';
import axiosInstance from '@/lib/axios';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#00F0FF', '#7000FF', '#00FF9F', '#FFD600', '#FF003C'];

export default function CompareSimulations({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSimulations();
  }, []);

  const loadSimulations = async () => {
    const simulationIds = location.state?.simulationIds || [];
    
    if (simulationIds.length === 0) {
      toast.error('Aucune simulation à comparer');
      navigate('/dashboard');
      return;
    }

    try {
      const promises = simulationIds.map(id =>
        axiosInstance.get(`${API}/simulations/${id}`, { withCredentials: true })
      );
      const responses = await Promise.all(promises);
      setSimulations(responses.map(r => r.data));
    } catch (error) {
      console.error('Error loading simulations:', error);
      toast.error('Erreur de chargement');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-cyan-500 border-t-transparent"></div>
          <p className="mt-4 text-cyan-400 font-['Rajdhani']">Chargement...</p>
        </div>
      </div>
    );
  }

  // Prepare comparison data
  const cashflowComparisonData = simulations.map((sim, index) => ({
    name: `${sim.location} (${sim.property_price.toLocaleString()}€)`,
    'Trad.': sim.cashflow_traditional,
    'Airbnb': sim.cashflow_airbnb,
  }));

  const loanComparisonData = simulations.map((sim, index) => ({
    name: `Bien ${index + 1}`,
    'Mensualité': sim.monthly_payment,
    'Coût crédit': sim.total_interest,
  }));

  const valueComparisonData = simulations.map((sim, index) => ({
    name: `Bien ${index + 1}`,
    'Valeur actuelle': sim.property_price,
    'Valeur 10 ans': sim.property_value_in_10y,
    'Valeur fin prêt': sim.property_value_at_loan_end,
  }));

  return (
    <div className="min-h-screen bg-[#030305] relative">
      <div className="cyber-grid-bg"></div>

      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/10 backdrop-blur-xl bg-black/20">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard"
                variant="ghost"
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Tableau de bord
              </Button>
              <h1 className="text-xl font-bold font-['Orbitron'] tracking-wider text-white">
                COMPARAISON DE <span className="text-cyan-400">SIMULATIONS</span>
              </h1>
              <div className="w-32"></div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-6 py-12 max-w-7xl">
          <div className="mb-8">
            <h2 className="text-3xl font-bold font-['Orbitron'] text-white mb-2">
              Analyse Comparative
            </h2>
            <p className="text-slate-400">
              Comparaison de {simulations.length} simulations d'investissement
            </p>
          </div>

          {/* Simulations Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {simulations.map((sim, index) => (
              <div
                key={sim.simulation_id}
                data-testid={`comparison-card-${index}`}
                className="glass-panel p-6 rounded-lg cursor-pointer hover:border-cyan-500/30 transition-all"
                onClick={() => navigate(`/simulation/${sim.simulation_id}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center font-bold"
                    style={{ backgroundColor: COLORS[index % COLORS.length] + '33', color: COLORS[index % COLORS.length] }}
                  >
                    {index + 1}
                  </div>
                  <div className="text-xs text-slate-500">
                    {new Date(sim.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-white mb-2">{sim.location}</h3>
                
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Prix</span>
                    <span className="text-white font-semibold">{sim.property_price.toLocaleString()} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Surface</span>
                    <span className="text-white">{sim.surface_m2} m² • {sim.nb_rooms}P</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Prêt</span>
                    <span className="text-white">{sim.loan_type}% • {sim.loan_duration} ans</span>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-400">Trad.</span>
                    <span className={`flex items-center gap-1 font-semibold text-sm ${
                      sim.cashflow_traditional > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {sim.cashflow_traditional > 0 ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : (
                        <TrendingDown className="h-3 w-3" />
                      )}
                      {sim.cashflow_traditional.toLocaleString()} €
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-400">Airbnb</span>
                    <span className={`flex items-center gap-1 font-semibold text-sm ${
                      sim.cashflow_airbnb > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {sim.cashflow_airbnb > 0 ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : (
                        <TrendingDown className="h-3 w-3" />
                      )}
                      {sim.cashflow_airbnb.toLocaleString()} €
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Comparison Charts */}
          <div className="space-y-8">
            {/* Cash Flow Comparison */}
            <div className="glass-panel p-6 rounded-lg">
              <h3 className="text-2xl font-bold font-['Rajdhani'] text-white mb-6">
                📊 Comparaison Cash-flow Mensuel
              </h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={cashflowComparisonData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="name" stroke="#94A3B8" angle={-15} textAnchor="end" height={100} />
                  <YAxis stroke="#94A3B8" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      border: '1px solid rgba(0, 240, 255, 0.3)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    formatter={(value) => `${value.toLocaleString()} €`}
                  />
                  <Legend />
                  <Bar dataKey="Trad." fill="#00F0FF" />
                  <Bar dataKey="Airbnb" fill="#7000FF" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Loan Cost Comparison */}
            <div className="glass-panel p-6 rounded-lg">
              <h3 className="text-2xl font-bold font-['Rajdhani'] text-white mb-6">
                💰 Comparaison Coûts de Crédit
              </h3>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {simulations.map((sim, index) => (
                  <div key={sim.simulation_id} className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs"
                        style={{ backgroundColor: COLORS[index % COLORS.length] + '33', color: COLORS[index % COLORS.length] }}
                      >
                        {index + 1}
                      </div>
                      <span className="text-white font-semibold">{sim.location}</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Durée</span>
                        <span className="text-white font-semibold">{sim.loan_duration} ans @ {sim.bank_rate}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Mensualité</span>
                        <span className="text-white font-semibold">{sim.monthly_payment.toLocaleString()} €</span>
                      </div>
                      <div className="flex justify-between text-sm border-t border-white/10 pt-2">
                        <span className="text-slate-400">Coût total crédit</span>
                        <span className="text-red-400 font-bold">{sim.total_interest.toLocaleString()} €</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={loanComparisonData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="name" stroke="#94A3B8" />
                  <YAxis stroke="#94A3B8" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      border: '1px solid rgba(0, 240, 255, 0.3)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    formatter={(value) => `${value.toLocaleString()} €`}
                  />
                  <Legend />
                  <Bar dataKey="Mensualité" fill="#00F0FF" />
                  <Bar dataKey="Coût crédit" fill="#FF003C" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Value Appreciation Comparison */}
            <div className="glass-panel p-6 rounded-lg">
              <h3 className="text-2xl font-bold font-['Rajdhani'] text-white mb-6">
                📈 Évolution de la Valeur des Biens
              </h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={valueComparisonData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="name" stroke="#94A3B8" />
                  <YAxis stroke="#94A3B8" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      border: '1px solid rgba(0, 240, 255, 0.3)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    formatter={(value) => `${value.toLocaleString()} €`}
                  />
                  <Legend />
                  <Bar dataKey="Valeur actuelle" fill="#94A3B8" />
                  <Bar dataKey="Valeur 10 ans" fill="#00FF9F" />
                  <Bar dataKey="Valeur fin prêt" fill="#FFD600" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Summary Table */}
            <div className="glass-panel p-6 rounded-lg">
              <h3 className="text-2xl font-bold font-['Rajdhani'] text-white mb-6">
                📋 Tableau Récapitulatif
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-2 text-slate-400 font-semibold">#</th>
                      <th className="text-left py-3 px-2 text-slate-400 font-semibold">Bien</th>
                      <th className="text-right py-3 px-2 text-slate-400 font-semibold">Prix</th>
                      <th className="text-right py-3 px-2 text-slate-400 font-semibold">CF Trad.</th>
                      <th className="text-right py-3 px-2 text-slate-400 font-semibold">CF Airbnb</th>
                      <th className="text-left py-3 px-2 text-slate-400 font-semibold">Stratégie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {simulations.map((sim, index) => (
                      <tr
                        key={sim.simulation_id}
                        className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                        onClick={() => navigate(`/simulation/${sim.simulation_id}`)}
                        data-testid={`table-row-${index}`}
                      >
                        <td className="py-3 px-2">
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs"
                            style={{ backgroundColor: COLORS[index % COLORS.length] + '33', color: COLORS[index % COLORS.length] }}
                          >
                            {index + 1}
                          </div>
                        </td>
                        <td className="py-3 px-2 text-white font-semibold">{sim.location}</td>
                        <td className="py-3 px-2 text-right text-white">{sim.property_price.toLocaleString()} €</td>
                        <td className={`py-3 px-2 text-right font-semibold ${
                          sim.cashflow_traditional > 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {sim.cashflow_traditional.toLocaleString()} €
                        </td>
                        <td className={`py-3 px-2 text-right font-semibold ${
                          sim.cashflow_airbnb > 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {sim.cashflow_airbnb.toLocaleString()} €
                        </td>
                        <td className="py-3 px-2 text-slate-300 text-xs">
                          {sim.recommended_strategy}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
